import type { FileUploadApiModel } from '@visiba/api-vcs-client-v2';
import { Http, HttpOptionConfiguration, HttpOptions } from '@visiba/http';
import { Injectable } from '@visiba/instantiation';
import { HostingService } from './hosting.service';
import { HttpResponseBody } from './http.service';
import { HttpError, isHttpError } from './http_error';

@Injectable()
export class HttpFileServer {
	constructor(private readonly hostingService: HostingService) {
		// Empty
	}

	public async identity(endpoint: string, name: string, size: number, type: string): Promise<FileUploadApiModel> {
		const headerBuilder: NonNullable<NonNullable<HttpOptionConfiguration['requestConfig']>['headers']> = {
			'Content-Type': 'application/json',
			Pragma: 'no-cache',
			'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
			'Cache-Control': 'no-cache, no-store, must-revalidate',
		};

		const options = new HttpOptions({
			requestConfig: {
				headers: headerBuilder,
			},
		});

		const response = await Http.post<VisibaBackendApiResponse<FileUploadApiModel>>(
			this.hostingService.apiBaseUri + '/' + endpoint,
			{
				Name: name,
				Length: size,
				Type: type,
			},
			options,
		);

		return response.data.Data;
	}

	public upload(file: File, absoluteUrl: string): [request: AsyncResult<HttpResponseBody<void>>, cancelSource: AbortController] {
		const formData = new FormData();
		formData.append('uploads[]', file, file.name);

		const cancelSource = Http.createCancelTokenSource();
		const options = new HttpOptions({
			cancelToken: cancelSource,
			requestConfig: {
				headers: {
					Pragma: 'no-cache',
					'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
					'Cache-Control': 'no-cache, no-store, must-revalidate',
				},
			},
		});

		const request = Http.post<void>(absoluteUrl, formData, options);

		// We have not migrated to fetch API with these methods yet. So we're still dependant on Axios via
		// our http module from cortex.
		// To bridge the gap between the two, we need to convert the response to act as if it was the fetch
		// variant instead.
		const patchedRequest = request
			.then((response) => {
				return Ok(response);
			})
			.catch((err) => {
				if (isHttpError(err)) {
					return err;
				}

				return Err(new HttpError('failed', 400, err));
			});

		const ac = new AbortController();
		ac.signal.addEventListener('abort', () => {
			cancelSource.cancel();
		});

		return [patchedRequest as AsyncResult<HttpResponseBody<void>>, ac];
	}

	public async remove(absoluteUrl: string): Promise<HttpResponseBody<void>> {
		const options = new HttpOptions({
			requestConfig: {
				withCredentials: true,
				headers: {
					'Content-Type': 'application/json',
					Pragma: 'no-cache',
					'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
					'Cache-Control': 'no-cache, no-store, must-revalidate',
				},
			},
		});

		const response = await Http.delete<void>(absoluteUrl, options);

		return {
			headers: response.headers as unknown as Headers,
			status: response.status,
			data: response.data,
		};
	}
}
